import { color, fontSize, radius } from '../../theme/tokens.js';

/**
 * The blueprint's segmented control — a track of options with the active one raised.
 *
 * Used for Source, Targeting, Specialty, grain, period, and the Table/Cards toggle. Rendered
 * as real `<button>`s inside a `role="group"` so the whole set is reachable by keyboard and
 * announced as a group, which the blueprint's `<div onClick>` version is not.
 */

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
  /** Announced instead of the short label where the label is a code such as `1M`. */
  accessibleLabel?: string;
  /**
   * An option that exists but cannot be chosen right now — the Alerts Timeline windows the checked
   * history cannot answer (spec 008, FR-AL24). `title` says why; it is also the accessible description.
   */
  disabled?: boolean;
  title?: string;
}

export interface SegmentedControlProps<T extends string> {
  options: Array<SegmentedOption<T>>;
  value: T;
  onChange: (value: T) => void;
  label: string;
}

export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  label,
}: SegmentedControlProps<T>) {
  return (
    <div
      role="group"
      aria-label={label}
      style={{
        display: 'flex',
        gap: 2,
        background: color.surfaceTrack,
        borderRadius: radius['2xl'],
        padding: 3,
      }}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => (option.disabled ? undefined : onChange(option.value))}
            aria-pressed={active}
            aria-label={option.accessibleLabel ?? `${label}: ${option.label}`}
            aria-disabled={option.disabled || undefined}
            title={option.title}
            style={{
              fontSize: fontSize.captionStrong,
              fontWeight: 600,
              padding: '5px 11px',
              borderRadius: radius.lg,
              cursor: option.disabled ? 'not-allowed' : 'pointer',
              opacity: option.disabled ? 0.45 : 1,
              border: 'none',
              fontFamily: 'inherit',
              background: active ? color.surface : 'transparent',
              color: active ? color.ink : color.inkMuted,
              boxShadow: active ? '0 1px 2px rgba(16,35,59,.12)' : 'none',
            }}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
